import { useEffect, useRef } from 'react'
import { AlertCircle, Check, CheckCheck, Clock } from 'lucide-react'
import { cn } from '../../lib/shadcn/utils'
import { MediaAttachment } from './MediaAttachment'
import { mediaKind, messageTime, tickFor, unshownNote, type ThreadMessage, type Tick } from '../../utils/whatsapp'

function UnshownNote({ message }: { message: ThreadMessage }) {
  const note = unshownNote(message)
  return (
    <p className={cn('italic text-muted-foreground', note.quiet && 'text-xs')}>{note.text}</p>
  )
}

function TickMark({ tick }: { tick: Tick }) {
  const cls = 'h-3.5 w-3.5'
  if (tick.glyph === 'failed')
    return <AlertCircle className={cn(cls, 'text-rose-600')} aria-label={tick.label} />
  if (tick.glyph === 'read') return <CheckCheck className={cn(cls, 'text-sky-600')} aria-label={tick.label} />
  if (tick.glyph === 'two') return <CheckCheck className={cls} aria-label={tick.label} />
  if (tick.glyph === 'one') return <Check className={cls} aria-label={tick.label} />
  return <Clock className={cls} aria-label={tick.label} />
}

export function ThreadView({ messages }: { messages: ThreadMessage[] }) {
  const endRef = useRef<HTMLDivElement | null>(null)
  const lastKey = messages.length ? messages[messages.length - 1].message_key : null

  // Scroll to the newest message when one arrives, not on every 15 second
  // refresh, so reading back up the thread is not yanked to the bottom.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' })
  }, [lastKey])

  if (messages.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        No messages in this conversation yet.
      </div>
    )
  }

  return (
    <div className="space-y-2 px-3 py-4 sm:px-6">
      {messages.map((m) => {
        const out = m.direction === 'out'
        const kind = mediaKind(m)
        const tick = out ? tickFor(m) : null
        return (
          <div key={m.message_key} className={cn('flex', out ? 'justify-end' : 'justify-start')}>
            <div
              className={cn(
                'max-w-[85%] rounded-lg px-3 py-2 text-sm shadow-sm sm:max-w-[70%]',
                out
                  ? 'bg-[hsl(var(--brand-soft))] text-foreground'
                  : 'border border-border bg-background text-foreground',
                tick?.glyph === 'failed' && 'border border-rose-300 dark:border-rose-800',
              )}
            >
              {out && m.template_key && (
                <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-[hsl(var(--brand-soft-foreground))]">
                  Template: {m.template_key}
                </p>
              )}
              {kind && <MediaAttachment message={m} kind={kind} />}
              {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
              {!m.body && !kind && <UnshownNote message={m} />}
              <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-muted-foreground">
                {out && m.sent_by_name && <span>{m.sent_by_name},</span>}
                <span className="tabular-nums">{messageTime(m.message_at)}</span>
                {tick && (
                  <span className="inline-flex items-center" title={tick.label}>
                    <TickMark tick={tick} />
                  </span>
                )}
              </div>
              {tick?.glyph === 'failed' && (
                <p className="mt-1 text-[11px] text-rose-700 dark:text-rose-300">{tick.label}</p>
              )}
              {!out && m.handled_at && (
                <p className="mt-0.5 text-[10px] text-muted-foreground">
                  Handled{m.handled_by_name ? ` by ${m.handled_by_name}` : ''}
                </p>
              )}
            </div>
          </div>
        )
      })}
      <div ref={endRef} />
    </div>
  )
}
